import { $generateNodesFromDOM } from '@lexical/html';
import is from '@sindresorhus/is';
import {
    $createParagraphNode,
    $isDecoratorNode,
    $isElementNode,
    $isTextNode,
    type ElementNode,
    type LexicalEditor,
    type LexicalNode,
} from 'lexical';

const isValidRootChild = (node: LexicalNode): boolean =>
    ($isElementNode(node) && node.isInline() === false) || ($isDecoratorNode(node) && node.isInline() === false);

export const createRootNodesFromHtml = (editor: LexicalEditor, html: string | undefined): LexicalNode[] => {
    if (is.undefined(html) || is.emptyString(html)) {
        return [$createParagraphNode()];
    }

    const parser = new DOMParser();
    const dom = parser.parseFromString(html, 'text/html');
    const nodes = $generateNodesFromDOM(editor, dom);
    const rootNodes: LexicalNode[] = [];
    let paragraph: ElementNode = $createParagraphNode();

    const appendParagraph = (): void => {
        if (paragraph.isEmpty() === false) {
            rootNodes.push(paragraph);
            paragraph = $createParagraphNode();
        }
    };

    for (const node of nodes) {
        if ($isTextNode(node) && node.getTextContent().trim() === '' && paragraph.isEmpty()) {
            continue;
        }

        if (isValidRootChild(node)) {
            appendParagraph();
            rootNodes.push(node);
        } else {
            paragraph.append(node);
        }
    }

    appendParagraph();

    if (rootNodes.length === 0) {
        return [$createParagraphNode()];
    }

    return rootNodes;
};

export const normalizeRichEditorHtml = (html: string): string => {
    const parser = new DOMParser();
    const dom = parser.parseFromString(html, 'text/html');
    const hasText = (dom.body.textContent ?? '').trim() !== '';
    const hasMeaningfulElement = Array.from(dom.body.querySelectorAll('hr,img,video,audio,iframe,table')).some(
        (element) => {
            const tagName = element.tagName.toLowerCase();
            const source = element.getAttribute('src');

            return tagName === 'hr' || tagName === 'table' || (source !== null && source.trim() !== '');
        },
    );

    return hasText || hasMeaningfulElement ? html : '';
};
